import { Paper, Typography, TextField, Button, IconButton, Box, Grid } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import type { PRPortfolioItem } from '../../../types/salesforce'
import { ImageUploadField } from './ImageUploadField'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface PortfolioSectionProps {
  prSheetId?: string
  portfolioItems: PRPortfolioItem[]
  onChange: (portfolioItems: PRPortfolioItem[]) => void
  onBlur?: () => void
}

function SortablePortfolioItem({
  item,
  index,
  onDelete,
  onChange,
  onBlur,
}: {
  item: PRPortfolioItem
  index: number
  onDelete: () => void
  onChange: (field: keyof PRPortfolioItem, value: string) => void
  onBlur?: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.Id! })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        p: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        backgroundColor: '#f9f9f9',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
            <DragIndicatorIcon />
          </IconButton>
          <Typography variant="subtitle2">#{index + 1}</Typography>
        </Box>
        <IconButton onClick={onDelete} size="small" color="error">
          <DeleteIcon />
        </IconButton>
      </Box>

      <TextField
        label="タイトル"
        fullWidth
        margin="normal"
        value={item.Title__c || ''}
        onChange={(e) => onChange('Title__c', e.target.value)}
        onBlur={onBlur}
        size="small"
      />

      <TextField
        label="URL（オプション）"
        fullWidth
        margin="normal"
        value={item.URL__c || ''}
        onChange={(e) => onChange('URL__c', e.target.value)}
        onBlur={onBlur}
        size="small"
        placeholder="https://example.com"
      />

      <ImageUploadField
        label="制作物イメージ"
        imageContentDocumentId={item.ImageContentDocumentId__c}
        onImageUpload={(contentDocumentId) => onChange('ImageContentDocumentId__c', contentDocumentId)}
        onImageDelete={() => onChange('ImageContentDocumentId__c', '')}
        onBlur={onBlur}
        circular={false}
        linkedEntityId={item.Id}
      />

      <TextField
        label="概要"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={item.Overview__c || ''}
        onChange={(e) => onChange('Overview__c', e.target.value)}
        onBlur={onBlur}
        size="small"
      />

      <TextField
        label="あなたの役割"
        fullWidth
        margin="normal"
        multiline
        rows={2}
        value={item.Role__c || ''}
        onChange={(e) => onChange('Role__c', e.target.value)}
        onBlur={onBlur}
        size="small"
      />
    </Box>
  )
}

export function PortfolioSection({ prSheetId, portfolioItems, onChange, onBlur }: PortfolioSectionProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const handleAdd = () => {
    const newItem: PRPortfolioItem = {
      Id: `temp-${Date.now()}`,
      PRSheet__c: prSheetId,
      Title__c: '',
      ImageContentDocumentId__c: '',
      Overview__c: '',
      Role__c: '',
      URL__c: '',
      SortOrder__c: (portfolioItems.length + 1) * 10,
    }
    onChange([...portfolioItems, newItem])
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = portfolioItems.findIndex((item) => item.Id === active.id)
      const newIndex = portfolioItems.findIndex((item) => item.Id === over.id)

      const reorderedItems = arrayMove(portfolioItems, oldIndex, newIndex)

      // SortOrder__cを更新
      const updatedItems = reorderedItems.map((item, index) => ({
        ...item,
        SortOrder__c: (index + 1) * 10,
      }))

      onChange(updatedItems)
      if (onBlur) onBlur()
    }
  }

  const handleChange = (index: number, field: keyof PRPortfolioItem, value: string) => {
    const newItems = [...portfolioItems]
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    }
    onChange(newItems)
  }

  const handleDelete = (index: number) => {
    const newItems = portfolioItems.filter((_, i) => i !== index)
    onChange(newItems)
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">🎨 制作ポートフォリオ</Typography>
        <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
          追加
        </Button>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={portfolioItems.map((item) => item.Id!)} strategy={rectSortingStrategy}>
          <Grid container spacing={3}>
            {portfolioItems.map((item, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={item.Id}>
                <SortablePortfolioItem
                  item={item}
                  index={index}
                  onDelete={() => handleDelete(index)}
                  onChange={(field, value) => handleChange(index, field, value)}
                  onBlur={onBlur}
                />
              </Grid>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>

      {portfolioItems.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          ポートフォリオアイテムを追加してください
        </Typography>
      )}
    </Paper>
  )
}
